// src/features/editor/components/studio/useDraftHistory.ts
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Block } from '@/types/blocks'
import { contentSignature } from '@/features/editor/lib/blockSignature'
import {
  DRAFT_COALESCE_MS,
  EMPTY_HISTORY,
  recordDraft,
  redoStep,
  undoStep,
  type DraftHistory,
  type HistoryStep,
} from '@/features/editor/lib/draftHistory'

export interface DraftHistoryResult {
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void
}

interface Steps {
  canUndo: boolean
  canRedo: boolean
}
const NO_STEPS: Steps = { canUndo: false, canRedo: false }

type TakeStep = (history: DraftHistory<Block>, current: Block) => HistoryStep<Block> | null

/**
 * История правок ОДНОГО открытого блока (п.11). Живёт отдельным хуком, а не
 * внутри `useBlockDraft`: тот уже несёт синхронизацию с сервером, конфликт
 * прогона и сохранение.
 *
 * Правки не перехватываются обёрткой над `setDraft` — хук СЛЕДИТ за `value`:
 * любое расхождение с тем, что история считает текущим, и есть правка автора.
 * Поэтому в `useBlockDraft` ничего не нужно оборачивать, а собственные шаги
 * (Undo/Redo) в историю не попадают — они обновляют этот же ориентир сами.
 *
 * @param value   текущий черновик
 * @param apply   применить снимок; обязан быть СТАБИЛЬНЫМ (это `setDraft`
 *                из `useState`) — на этом стоит стабильность `undo`/`redo`,
 *                без которой мемоизация значения контекста бессмысленна
 * @param base    ОСНОВА черновика (последний увиденный серверный снимок).
 *                Её смена — единственное событие, обнуляющее историю: выбран
 *                другой блок, принята серверная версия (молча или кнопкой),
 *                прошло сохранение. Без обнуления Undo воскресил бы тело,
 *                каким оно было ДО платной правки прогона, и молча её откатил.
 * @param isLocked блок прямо сейчас переписывает прогон — шаги объявляются
 *                недоступными (трогать занятый блок нельзя ничем, включая
 *                откат). Сам буфер при этом не чистится, но «вернутся те же
 *                шаги» обещать нельзя: если прогон переписал ИМЕННО этот блок
 *                и его результат будет принят (молча — при нетронутом
 *                черновике, или кнопкой «взять версию прогона»), основа
 *                сдвинется и историю обнулит. Шаги переживают только прогон,
 *                который блока не коснулся, либо путь конфликта, где черновик
 *                автора остаётся расходиться с сервером.
 */
export const useDraftHistory = (
  value: Block | null,
  apply: (next: Block) => void,
  base: Block | null,
  isLocked: boolean,
  /** Основа, поставленная своим сохранением: шаги правок автора переживают её. */
  ownSaved: Block | null = null,
): DraftHistoryResult => {
  const historyRef = useRef<DraftHistory<Block>>(EMPTY_HISTORY)
  const valueRef = useRef<Block | null>(value)
  /** Когда сделан последний снимок — начало окна схлопывания серии. */
  const lastRecordAtRef = useRef(0)
  const [steps, setSteps] = useState<Steps>(NO_STEPS)

  const commit = useCallback((next: DraftHistory<Block>) => {
    historyRef.current = next
    setSteps((prev) => {
      const canUndo = next.past.length > 0
      const canRedo = next.future.length > 0
      // Ссылка сохраняется, пока доступность шагов не изменилась: `record`
      // зовётся на КАЖДОЕ нажатие клавиши в блоке, а на нём висит мемоизация
      // значения контекста шапки.
      return prev.canUndo === canUndo && prev.canRedo === canRedo ? prev : { canUndo, canRedo }
    })
  }, [])

  // Обнуление на смене основы. Правка состояния во время рендера намеренна:
  // это документированный React-приём «поправить состояние при смене входа», и
  // он не даёт истории пережить принятие серверной версии даже на один кадр.
  const baseRef = useRef<Block | null>(base)
  if (baseRef.current !== base && base !== null && base === ownSaved) {
    // Своё (авто)сохранение: в истории только шаги самого автора, откат к ним
    // ничего чужого не воскресит — историю оставляем, переход правкой не считаем.
    baseRef.current = base
    valueRef.current = value
  } else if (baseRef.current !== base) {
    baseRef.current = base
    historyRef.current = EMPTY_HISTORY
    // Переход, вызванный самой сменой основы, правкой автора не является.
    valueRef.current = value
    lastRecordAtRef.current = 0
    setSteps(NO_STEPS)
  }

  useEffect(() => {
    const previous = valueRef.current
    if (previous === value) return
    valueRef.current = value
    if (!previous || !value) return
    // `order` меняет реордер, а не автор: снимок ради него не заводим.
    if (contentSignature(previous) === contentSignature(value)) return
    // Серия схлопывается в ОДИН шаг: поля редактора пишут блок целиком на
    // каждое нажатие клавиши, и без окна десять шагов истории означали бы
    // десять СИМВОЛОВ, а не десять правок. Снимок берётся на первой правке
    // серии — это и есть состояние «до того, как автор начал»; остальные
    // правки окна пропускаются. Redo-хвост при этом не осиротеет: шаг истории
    // сбрасывает отметку (см. `move`), поэтому правка после Undo всегда
    // записывается и хвост обрезает.
    const at = Date.now()
    if (at - lastRecordAtRef.current < DRAFT_COALESCE_MS) return
    lastRecordAtRef.current = at
    commit(recordDraft(historyRef.current, previous))
  }, [value, commit])

  const move = useCallback(
    (take: TakeStep) => {
      const current = valueRef.current
      if (!current) return
      const step = take(historyRef.current, current)
      if (!step) return
      // Позицией владеет реордер: снимок несёт `order` времён правки, и его
      // восстановление увело бы блок на прежнее место при ближайшем `PUT`.
      const next: Block = { ...step.value, order: current.order }
      valueRef.current = next
      // Шаг истории начинает серию заново: правка сразу после Undo обязана
      // записаться, иначе она не обрежет redo-хвост и Redo стёр бы её.
      lastRecordAtRef.current = 0
      commit(step.history)
      apply(next)
    },
    [apply, commit],
  )

  const undo = useCallback(() => move(undoStep), [move])
  const redo = useCallback(() => move(redoStep), [move])

  return { canUndo: steps.canUndo && !isLocked, canRedo: steps.canRedo && !isLocked, undo, redo }
}
